import React from 'react';
import { AlertCircle, Award, Briefcase, CheckCircle, Clock, FileCheck, RefreshCw, Shield, X } from 'lucide-react';

// Extracted from EmployerDashboard.js — receives everything it needs via `ctx`.
export default function EmployerShiftRequestsModal({ ctx }) {
  const { handleApproveShiftRequest, handleRejectShiftRequest, loadShiftRequests, setShowShiftRequests, shiftRequests, shiftRequestsLoading, showShiftRequests } = ctx;
  return (
    <>
    {showShiftRequests && (
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-500 to-orange-500 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                <FileCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Shift Requests</h2>
                <p className="text-sm text-white/80">Review and approve worker applications</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadShiftRequests}
                disabled={shiftRequestsLoading}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 text-white ${shiftRequestsLoading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={() => setShowShiftRequests(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition"
                data-testid="close-shift-requests-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {shiftRequestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : shiftRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex p-4 bg-slate-100 rounded-2xl mb-4">
                  <FileCheck className="w-12 h-12 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Pending Requests</h3>
                <p className="text-slate-500">Worker applications for your shifts will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {shiftRequests.map((request) => (
                  <div 
                    key={request._id} 
                    className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:shadow-md transition"
                    data-testid={`shift-request-${request._id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Worker Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0 overflow-hidden">
                          {request.worker?.profile_photo ? (
                            <img src={request.worker.profile_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            request.worker?.full_name?.charAt(0)?.toUpperCase() || 'W'
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 text-lg">{request.worker?.full_name}</h4>
                          <p className="text-sm text-slate-500">{request.worker?.email}</p>
                          <div className="flex flex-wrap items-center gap-3 mt-2">
                            {request.worker?.rating > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium">
                                <Award className="w-3 h-3" />
                                {request.worker.rating.toFixed(1)} rating
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                              <Briefcase className="w-3 h-3" />
                              {request.worker?.completed_shifts || 0} shifts completed
                            </span>
                            {request.worker?.background_verified ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-semibold" data-testid={`bg-verified-badge-${request.worker?._id}`} title="Passed a Checkr background check with a clear result">
                                <Shield className="w-3 h-3" />
                                Background Verified
                              </span>
                            ) : (request.worker?.checkr_status === 'invited' || request.worker?.checkr_status === 'pending') ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold" data-testid={`bg-pending-badge-${request.worker?._id}`} title="Background check in progress with Checkr">
                                <Clock className="w-3 h-3" />
                                Check in progress
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold" data-testid={`bg-unverified-badge-${request.worker?._id}`} title="No background check on file — you can run one from the Bg Checks tab">
                                <AlertCircle className="w-3 h-3" />
                                Not background checked
                              </span>
                            )}
                          </div>
                          {request.worker?.skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {request.worker.skills.slice(0, 3).map((skill, idx) => (
                                <span key={`${skill}-${idx}`} className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-xs">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Shift Info */}
                      <div className="md:text-right">
                        <div className="inline-block px-4 py-2 bg-white border border-slate-200 rounded-xl mb-3">
                          <p className="font-semibold text-slate-900">{request.shift?.title}</p>
                          <p className="text-sm text-slate-500">
                            {request.shift?.start_time && new Date(request.shift.start_time).toLocaleDateString()}
                          </p>
                          <p className="text-emerald-600 font-bold">${request.shift?.pay_rate}/hr</p>
                        </div>
                        <p className="text-xs text-slate-400">
                          Applied {request.applied_at && new Date(request.applied_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => {
                          const reason = window.prompt('Enter rejection reason (optional):');
                          handleRejectShiftRequest(request._id, reason);
                        }}
                        className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition text-sm font-medium"
                        data-testid={`reject-request-${request._id}`}
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApproveShiftRequest(request._id)}
                        className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition text-sm font-medium flex items-center gap-2"
                        data-testid={`approve-request-${request._id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve & Assign
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
